import React, { useEffect, useState } from 'react'
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Link, MenuItem, TextField, Typography } from '@mui/material'
import { LoadingButton } from '@mui/lab'
import { Link as RouterLink } from 'react-router-dom'
import { useSnackbar } from 'notistack'
import { C, RADIUS, SX, TYPE } from '../../merchants/shared/tokens'
import { errorText, fetchTatCouriers, setTatBasis } from '../ptlSetupApi'
import { num, ROUTES, tatBaseName } from '../constants'

/**
 * Where a merchant's non-custom lanes get their TAT, as a sentence: "the Prozo standard on all 256 lanes" or "the
 * B2B_BLUEDART_INT TAT on 3 lanes and the Prozo standard on the other 253". From the grid counts (tat/grid).
 */
export const baseSummary = (grid) => (grid.basisCourier && grid.courierLanes
  ? `the ${grid.basisCourier} TAT on ${num(grid.courierLanes)} lane${grid.courierLanes === 1 ? '' : 's'} and the Prozo standard on the other ${num(grid.standardLanes)}`
  : `the Prozo standard on all ${num(grid.standardLanes)} lanes`)

/**
 * TAT basis of one merchant: Standard TAT (default) or one courier's own TAT (Merchant TAT → Courier TAT), for a
 * merchant that ships with that courier only. Picking a card only changes the screen; the save is the confirm dialog
 * (POST tat/basis). Used by onboarding Step 2 and Merchant TAT → Custom TAT → a merchant.
 *
 * @param extra   optional third card beside the two choices (Step 2: the Custom TAT card)
 * @param onSaved called after the basis is saved
 */
export default function TatBasis({ merchant, grid, onSaved, extra }) {
  const { enqueueSnackbar } = useSnackbar()
  const [couriers, setCouriers] = useState(null)
  // Basis being confirmed: '' = standard, a prozoId = that courier; null = no dialog.
  const [basisTo, setBasisTo] = useState(null)
  const [pickCourier, setPickCourier] = useState('')
  const [listOpen, setListOpen] = useState(false)
  // "Courier TAT" picked on screen but no courier saved yet: shows the courier picker.
  const [courierMode, setCourierMode] = useState(false)
  const [busy, setBusy] = useState(false)

  useEffect(() => { fetchTatCouriers().then(setCouriers).catch(() => setCouriers([])) }, [])

  const basis = grid.basisCourier || ''
  const customCount = grid.customLanes || 0
  // Couriers that can be a basis: live, with their TAT uploaded. Ones with a rate card for this merchant first. The
  // saved basis stays listed even after its account stops being live: pricing still uses its TAT until it is changed.
  const carded = new Set(merchant.rateCardCouriers || [])
  const options = (couriers || []).filter((c) => (!c.notLive && c.lanes > 0) || c.courier === basis)
    .sort((a, b) => Number(carded.has(b.courier)) - Number(carded.has(a.courier)) || a.courier.localeCompare(b.courier))
  const noCourierTat = couriers !== null && options.length === 0
  const basisNotLive = !!basis && (couriers || []).some((c) => c.courier === basis && c.notLive)
  useEffect(() => { setPickCourier(basis); setCourierMode(false) }, [basis])
  const showCourier = !!basis || courierMode
  // Opens the courier list once the picker is on screen (the select needs to be mounted to anchor its menu).
  useEffect(() => { if (courierMode && options.length) setListOpen(true) }, [courierMode]) // eslint-disable-line react-hooks/exhaustive-deps

  const save = async () => {
    setBusy(true)
    try {
      await setTatBasis(merchant.id, basisTo)
      enqueueSnackbar(`${merchant.name} is now on ${tatBaseName(basisTo)}`, { variant: 'success' })
      setBasisTo(null)
      onSaved()
    } catch (e) {
      enqueueSnackbar(errorText(e, 'Could not change the TAT basis'), { variant: 'error' })
    } finally {
      setBusy(false)
    }
  }

  // selected: picked on screen; inUse: the saved basis. disabled: nothing to pick yet (no courier TAT uploaded); the box
  // below says where to upload one.
  const choice = (title, body, selected, onPick, { inUse = false, disabled = false } = {}) => (
    <Box component='div' role='radio' aria-checked={selected} aria-disabled={disabled || undefined} tabIndex={disabled ? -1 : 0}
      onClick={disabled ? undefined : onPick}
      onKeyDown={(e) => { if (!disabled && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); onPick() } }}
      sx={{
        boxSizing: 'border-box', cursor: selected || disabled ? 'default' : 'pointer', display: 'block', p: 2, minHeight: 96,
        borderRadius: RADIUS.md, border: `1px solid ${selected ? C.accent : C.line}`, bgcolor: selected ? C.accentSoft : C.surface,
        opacity: disabled ? 0.6 : 1,
        transition: 'border-color 150ms, background-color 150ms',
        '&:hover': selected || disabled ? {} : { borderColor: C.accentBorder },
        '&:focus-visible': { outline: `2px solid ${C.accent}`, outlineOffset: 2 },
      }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
        <Box aria-hidden sx={{
          width: 18, height: 18, borderRadius: '50%', display: 'grid', placeItems: 'center', flexShrink: 0,
          border: `2px solid ${selected ? C.accent : C.lineStrong}`, bgcolor: selected ? C.accent : 'transparent',
        }}>
          {selected && <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#fff' }} />}
        </Box>
        <Typography component='span' sx={{ ...TYPE.rowLabel, fontSize: 14.5 }}>{title}</Typography>
        {inUse && <Box component='span' sx={{ ...SX.pill, ml: 'auto', color: C.ok, bgcolor: C.okSoft, border: `1px solid ${C.okBorder}` }}>In use</Box>}
      </Box>
      <Typography component='span' sx={{ ...TYPE.rowHelp, display: 'block', pl: 3.25 }}>{body}</Typography>
    </Box>
  )

  const courierPicker = (
    <Box sx={{ ...SX.cardFlush, p: 2, mt: 1.25, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
      {couriers === null ? (
        <Typography sx={TYPE.rowHelp}>Loading couriers…</Typography>
      ) : options.length === 0 ? (
        <Typography sx={TYPE.rowHelp}>
          No courier TAT is uploaded yet.{' '}
          <Link component={RouterLink} to={ROUTES.tatCouriers}>Upload one in Merchant TAT → Courier TAT</Link>.
        </Typography>
      ) : (
        <>
          <TextField select size='small' label='Courier' value={pickCourier} onChange={(e) => setPickCourier(e.target.value)} sx={{ minWidth: 260, bgcolor: C.surface }}
            SelectProps={{ open: listOpen, onOpen: () => setListOpen(true), onClose: () => setListOpen(false) }}>
            {options.map((c) => (
              <MenuItem key={c.courier} value={c.courier} disabled={c.notLive}>
                {c.courier}{c.notLive ? ' · not live' : carded.has(c.courier) ? ' · has a rate card' : ''}
              </MenuItem>
            ))}
          </TextField>
          <Button variant='contained' disabled={!pickCourier || pickCourier === basis} onClick={() => setBasisTo(pickCourier)} sx={{ ...SX.cta, minHeight: 36 }}>
            {basis ? 'Change courier' : 'Use this courier'}
          </Button>
          {basisNotLive && pickCourier === basis && (
            <Typography sx={{ ...TYPE.rowHelp, color: C.warn, flexBasis: '100%' }}>
              {basis} is no longer a live PTL courier account, but {merchant.name} still gets its TAT. Pick another courier or Standard TAT.
            </Typography>
          )}
          {pickCourier && !carded.has(pickCourier) && !(basisNotLive && pickCourier === basis) && (
            <Typography sx={{ ...TYPE.rowHelp, color: C.warn, flexBasis: '100%' }}>
              {merchant.name} has no rate card for {pickCourier}. Its TAT can still be used; add the rate card in Step 1 if it will ship with it.
            </Typography>
          )}
        </>
      )}
    </Box>
  )

  return (
    <Box>
      <Typography component='h2' sx={TYPE.sectionTitle}>TAT basis</Typography>
      <Typography sx={{ ...TYPE.rowHelp, mt: 0.25, mb: 1.5 }}>
        Working days, Monday to Saturday. EDD uses the MAX. Pick one base: Standard TAT, or Courier TAT only when the merchant
        ships with that one courier. Custom TAT is optional, on top of the base.
      </Typography>
      <Box sx={{ display: 'grid', gap: 1.25, gridTemplateColumns: { xs: '1fr', md: extra ? '2fr 1fr' : '1fr' } }}>
        <Box role='radiogroup' aria-label='Base TAT' sx={{ display: 'grid', gap: 1.25, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' } }}>
          {choice('Standard TAT',
            'Every lane uses the Prozo standard MIN-MAX and moves with it when the standard is updated. Nothing to upload.',
            !showCourier, () => (basis ? setBasisTo('') : setCourierMode(false)), { inUse: !basis })}
          {choice('Courier TAT',
            'The chosen courier’s own TAT on the lanes it gives; the Prozo standard on the rest. Moves with that courier’s TAT.',
            showCourier, () => (showCourier ? setListOpen(true) : setCourierMode(true)), { inUse: !!basis, disabled: !basis && noCourierTat })}
        </Box>
        {extra}
      </Box>
      {(showCourier || noCourierTat) && courierPicker}

      <Dialog open={basisTo !== null} onClose={() => !busy && setBasisTo(null)} maxWidth='xs' fullWidth>
        <DialogTitle sx={{ ...TYPE.sectionTitle, fontSize: 16 }}>Put {merchant.name} on {tatBaseName(basisTo)}?</DialogTitle>
        <DialogContent>
          <Typography sx={{ ...TYPE.rowHelp, fontSize: 13.5, color: C.inkBody }}>
            {basisTo
              ? `Shipments priced from now on get the ${basisTo} TAT on the lanes it gives and the Prozo standard on the rest.`
              : `Shipments priced from now on get the Prozo standard TAT on every ${customCount ? 'other ' : ''}lane.`}
            {customCount ? ` Its ${num(customCount)} custom lane${customCount === 1 ? '' : 's'} stay and still win.` : ''} Existing shipments keep their EDD.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setBasisTo(null)} disabled={busy} sx={{ textTransform: 'none', color: C.inkMuted }}>Cancel</Button>
          <LoadingButton variant='contained' loading={busy} onClick={save} sx={SX.cta}>{basisTo ? `Use the ${basisTo} TAT` : 'Use the standard'}</LoadingButton>
        </DialogActions>
      </Dialog>
    </Box>
  )
}
